'use client';

/**
 * @fileoverview Reusable 3×3 home / apps grid icon with optional hover-driven motion.
 * @purpose Shared visual for `AppsHomeLink`; picks a random animation while “hot” (hover/focus).
 * @ownership Core package
 */

import './HomeGridIcon.css';

import type { CSSProperties, SVGProps } from 'react';
import { useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';

import {
  HOME_GRID_CELL_RX,
  HOME_GRID_CELL_SIZE,
  HOME_GRID_CELL_TOP_LEFT,
} from './homeGridConstants';
import {
  applyRandomLegalMove,
  findSingleSlidingTile,
  PUZZLE_SOLVED,
  PUZZLE_TILE_IDS,
  shufflePuzzle,
} from './slidingPuzzleLayout';

const HALF = HOME_GRID_CELL_SIZE / 2;

function slotCenter(slot: number): { cx: number; cy: number } {
  const [left, top] = HOME_GRID_CELL_TOP_LEFT[slot]!;
  return { cx: left + HALF, cy: top + HALF };
}

export type HomeGridIconAnimation =
  | 'rubik'
  | 'puzzle'
  | 'hue'
  | 'jiggle'
  | 'wave'
  | 'breathe'
  | 'twinkle'
  | 'tilt'
  | 'seesaw'
  | 'core'
  | 'orbit'
  | 'slots'
  | 'neon';

const ANIMATIONS: HomeGridIconAnimation[] = [
  'rubik',
  'puzzle',
  'hue',
  'jiggle',
  'wave',
  'breathe',
  'twinkle',
  'tilt',
  'seesaw',
  'core',
  'orbit',
  'slots',
  'neon',
];

/** Pick a random animation id (call on each hover/focus enter for variety). */
export function pickRandomHomeGridAnimation(): HomeGridIconAnimation {
  return ANIMATIONS[Math.floor(Math.random() * ANIMATIONS.length)]!;
}

export type HomeGridIconProps = Omit<SVGProps<SVGSVGElement>, 'children'> & {
  /** Active motion variant; `null` = static grid. Respects `prefers-reduced-motion` via CSS. */
  activeAnimation?: HomeGridIconAnimation | null;
  /** When true, each tile uses a distinct fill (hover/hot state). Idle uses `idleFill`. */
  chromatic?: boolean;
  /** Fill for every tile when not `chromatic` (default white; use e.g. `var(--primary-foreground)` on primary buttons). */
  idleFill?: string;
  /** Wrapper class for the span around the svg (layout in flex rows). */
  wrapperClassName?: string;
};

function subscribeReducedMotion(callback: () => void): () => void {
  const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
  mq.addEventListener('change', callback);
  return () => mq.removeEventListener('change', callback);
}

function getReducedMotionSnapshot(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function usePrefersReducedMotion(): boolean {
  return useSyncExternalStore(subscribeReducedMotion, getReducedMotionSnapshot, () => false);
}

const PUZZLE_MOVE_MIN_MS = 95;
const PUZZLE_MOVE_JITTER_MS = 85;
const PUZZLE_SHUFFLE_STEPS = 110;
const PUZZLE_SLIDE_MS = 165;

/**
 * 3×3 rounded squares. Idle tiles use `idleFill`; when `chromatic`, tiles get distinct colors (hover in `AppsHomeLink`).
 */
export function HomeGridIcon({
  className,
  style,
  activeAnimation = null,
  chromatic = false,
  idleFill = '#ffffff',
  wrapperClassName,
  ...svgRest
}: HomeGridIconProps) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [puzzleLayout, setPuzzleLayout] = useState<(number | null)[]>(() => [...PUZZLE_SOLVED]);
  /** Browser timers use numeric ids; avoid `NodeJS.Timeout` from ambient `setTimeout` typing. */
  const puzzleTimeoutRef = useRef<number | null>(null);
  /** Grid index where the hole was before the last move; excludes immediate undo when picking the next neighbor. */
  const puzzleHoleCameFromRef = useRef<number | null>(null);
  const prevPuzzleRef = useRef<(number | null)[] | null>(null);
  const slideRafRef = useRef<number>(0);
  const [slideDraw, setSlideDraw] = useState<{ tileId: number; cx: number; cy: number } | null>(null);

  const mod =
    activeAnimation != null && activeAnimation !== undefined
      ? `korvoHomeGrid--${activeAnimation}`
      : '';

  const spanStyle: CSSProperties = {
    ['--korvo-home-idle-fill' as string]: idleFill,
  };

  useEffect(() => {
    if (activeAnimation !== 'puzzle') {
      setPuzzleLayout([...PUZZLE_SOLVED]);
      puzzleHoleCameFromRef.current = null;
      prevPuzzleRef.current = null;
      if (puzzleTimeoutRef.current != null) {
        clearTimeout(puzzleTimeoutRef.current);
        puzzleTimeoutRef.current = null;
      }
      return undefined;
    }
    if (prefersReducedMotion) {
      setPuzzleLayout([...PUZZLE_SOLVED]);
      puzzleHoleCameFromRef.current = null;
      prevPuzzleRef.current = null;
      return undefined;
    }

    puzzleHoleCameFromRef.current = null;
    setPuzzleLayout(shufflePuzzle(PUZZLE_SOLVED, PUZZLE_SHUFFLE_STEPS));

    const scheduleStep = () => {
      puzzleTimeoutRef.current = window.setTimeout(() => {
        setPuzzleLayout((prev) => {
          const emptyBefore = prev.indexOf(null);
          const next = applyRandomLegalMove(prev, { avoidSlot: puzzleHoleCameFromRef.current });
          puzzleHoleCameFromRef.current = emptyBefore;
          return next;
        });
        scheduleStep();
      }, PUZZLE_MOVE_MIN_MS + Math.random() * PUZZLE_MOVE_JITTER_MS);
    };
    scheduleStep();

    return () => {
      if (puzzleTimeoutRef.current != null) {
        clearTimeout(puzzleTimeoutRef.current);
        puzzleTimeoutRef.current = null;
      }
    };
  }, [activeAnimation, prefersReducedMotion]);

  const isPuzzle = activeAnimation === 'puzzle';

  useLayoutEffect(() => {
    if (!isPuzzle || prefersReducedMotion) {
      prevPuzzleRef.current = isPuzzle ? [...puzzleLayout] : null;
      if (slideRafRef.current) {
        cancelAnimationFrame(slideRafRef.current);
        slideRafRef.current = 0;
      }
      setSlideDraw(null);
      return () => {
        if (slideRafRef.current) {
          cancelAnimationFrame(slideRafRef.current);
          slideRafRef.current = 0;
        }
      };
    }

    const prev = prevPuzzleRef.current;
    prevPuzzleRef.current = [...puzzleLayout];

    if (prev === null) {
      setSlideDraw(null);
      return () => {
        if (slideRafRef.current) {
          cancelAnimationFrame(slideRafRef.current);
          slideRafRef.current = 0;
        }
      };
    }

    const move = findSingleSlidingTile(prev, puzzleLayout);
    if (move === null) {
      setSlideDraw(null);
      return () => {
        if (slideRafRef.current) {
          cancelAnimationFrame(slideRafRef.current);
          slideRafRef.current = 0;
        }
      };
    }

    if (slideRafRef.current) {
      cancelAnimationFrame(slideRafRef.current);
    }

    const fromC = slotCenter(move.fromSlot);
    const toC = slotCenter(move.toSlot);
    const startWall = performance.now();

    setSlideDraw({ tileId: move.tileId, cx: fromC.cx, cy: fromC.cy });

    const ease = (p: number) => p * p * (3 - 2 * p);

    const tick = (now: number) => {
      const t = Math.min(1, (now - startWall) / PUZZLE_SLIDE_MS);
      const e = ease(t);
      setSlideDraw({
        tileId: move.tileId,
        cx: fromC.cx + (toC.cx - fromC.cx) * e,
        cy: fromC.cy + (toC.cy - fromC.cy) * e,
      });
      if (t < 1) {
        slideRafRef.current = requestAnimationFrame(tick);
      } else {
        slideRafRef.current = 0;
        setSlideDraw(null);
      }
    };

    slideRafRef.current = requestAnimationFrame(tick);

    return () => {
      if (slideRafRef.current) {
        cancelAnimationFrame(slideRafRef.current);
        slideRafRef.current = 0;
      }
    };
  }, [puzzleLayout, isPuzzle, prefersReducedMotion]);

  return (
    <span
      className={['korvoHomeGrid', mod, chromatic ? 'korvoHomeGrid--chromatic' : '', wrapperClassName]
        .filter(Boolean)
        .join(' ')}
      style={spanStyle}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        className={className}
        style={style}
        aria-hidden
        {...svgRest}
      >
        <g className="korvoHomeGrid__matrix">
          {isPuzzle
            ? PUZZLE_TILE_IDS.map((tileId: (typeof PUZZLE_TILE_IDS)[number]) => {
                const slot = puzzleLayout.indexOf(tileId);
                if (slot < 0) {
                  return null;
                }
                const { cx, cy } = slotCenter(slot);
                const draw = slideDraw;
                const sliding = draw !== null && draw.tileId === tileId;
                const tx = sliding ? draw.cx : cx;
                const ty = sliding ? draw.cy : cy;
                return (
                  <g key={tileId} data-korvo-tile={tileId} transform={`translate(${tx},${ty})`}>
                    <g className="korvoHomeGrid__cell">
                      <rect
                        x={-HALF}
                        y={-HALF}
                        width={HOME_GRID_CELL_SIZE}
                        height={HOME_GRID_CELL_SIZE}
                        rx={HOME_GRID_CELL_RX}
                      />
                    </g>
                  </g>
                );
              })
            : HOME_GRID_CELL_TOP_LEFT.map(([left, top], i) => {
                const cx = left + HALF;
                const cy = top + HALF;
                return (
                  <g key={i} transform={`translate(${cx}, ${cy})`} data-korvo-cell={i}>
                    <g className="korvoHomeGrid__cell">
                      <rect
                        x={-HALF}
                        y={-HALF}
                        width={HOME_GRID_CELL_SIZE}
                        height={HOME_GRID_CELL_SIZE}
                        rx={HOME_GRID_CELL_RX}
                      />
                    </g>
                  </g>
                );
              })}
        </g>
      </svg>
    </span>
  );
}
